import { useEffect, useMemo, useState } from 'react';
import * as adminService from '@/services/adminService';

const typeLabels = {
  mission_complete: 'Missão concluída',
  badge_unlock: 'Medalha desbloqueada',
  friend_request: 'Pedido de amizade',
  offer_expiring: 'Oferta a expirar',
  level_up: 'Subida de nível',
  system: 'Sistema',
};

const typeBadgeClass = (type) => {
  switch (type) {
    case 'mission_complete': return 'border-forest-light/30 bg-forest-light/10 text-forest-light';
    case 'badge_unlock': return 'border-accent/30 bg-accent/10 text-accent';
    case 'friend_request': return 'border-ocean-light/30 bg-ocean-light/10 text-ocean-light';
    case 'offer_expiring': return 'border-energy/30 bg-energy/10 text-energy';
    case 'level_up': return 'border-purple-400/30 bg-purple-400/10 text-purple-300';
    default: return 'border-fg/15 bg-fg/5 text-fg/60';
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('pt-PT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(value));
};

const fieldClass = 'rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent';

export default function AdminNotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState('all');
  const [page, setPage] = useState(1);
  const limit = 50;

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filterType !== 'all') params.type = filterType;
      if (filterRead !== 'all') params.isRead = filterRead;
      const res = await adminService.getNotifications(params);
      setNotifications(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
      setError('');
    } catch {
      setError('Não foi possível carregar as notificações.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [filterType, filterRead, page]);

  const stats = useMemo(() => ({
    total: total,
    unread: notifications.filter((n) => !n.isRead).length,
  }), [notifications, total]);

  const handleMarkRead = async (id) => {
    try {
      await adminService.markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
    } catch {
      setError('Não foi possível marcar como lida.');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await adminService.markAllNotificationsRead();
      setNotice('Todas as notificações foram marcadas como lidas.');
      await loadData();
    } catch {
      setError('Não foi possível marcar todas como lidas.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta notificação?')) return;
    try {
      await adminService.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setTotal((prev) => prev - 1);
    } catch {
      setError('Não foi possível remover a notificação.');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Sistema</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Notificações</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Notificações enviadas aos jogadores — missões, medalhas, ofertas e alertas do sistema.
          </p>
        </div>
        <button
          type="button"
          onClick={handleMarkAllRead}
          className="rounded-lg border border-forest-light/40 bg-forest-light/10 px-4 py-2 text-sm font-bold text-forest-light hover:bg-forest-light/20"
        >
          Marcar todas como lidas
        </button>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-3">
        {[
          ['Total (página)', notifications.length],
          ['Total geral', total],
          ['Não lidas (página)', stats.unread],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">{label}</div>
            <div className="mt-2 font-serif text-3xl font-black text-accent">{value}</div>
          </div>
        ))}
      </div>

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Lista de notificações</h2>
          <div className="flex flex-wrap gap-2">
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
              className={fieldClass}
            >
              <option value="all">Todos os tipos</option>
              {Object.entries(typeLabels).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
            <select
              value={filterRead}
              onChange={(e) => { setFilterRead(e.target.value); setPage(1); }}
              className={fieldClass}
            >
              <option value="all">Todas</option>
              <option value="false">Não lidas</option>
              <option value="true">Lidas</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Jogador</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Título</th>
                <th className="px-4 py-3">Mensagem</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {loading && (
                <tr>
                  <td className="px-4 py-6 text-fg/40" colSpan="7">A carregar...</td>
                </tr>
              )}
              {!loading && notifications.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-fg/40" colSpan="7">Nenhuma notificação encontrada.</td>
                </tr>
              )}
              {notifications.map((n) => (
                <tr key={n.id} className={`align-top ${n.isRead ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="font-bold text-fg">{n.player?.displayName || n.player?.username || '—'}</div>
                    <div className="mt-0.5 text-xs text-fg/35">{n.playerId?.slice(0, 8)}…</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${typeBadgeClass(n.type)}`}>
                      {typeLabels[n.type] || n.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-bold text-fg">{n.title}</td>
                  <td className="px-4 py-3 max-w-xs text-fg/55">{n.body || '—'}</td>
                  <td className="px-4 py-3 text-fg/50">{formatDate(n.createdAt)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${n.isRead ? 'border-fg/10 bg-fg/5 text-fg/35' : 'border-ocean-light/30 bg-ocean-light/10 text-ocean-light'}`}>
                      {n.isRead ? 'Lida' : 'Não lida'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!n.isRead && (
                      <button
                        type="button"
                        onClick={() => handleMarkRead(n.id)}
                        className="mr-2 rounded-lg border border-forest-light/30 px-3 py-1.5 text-xs font-bold text-forest-light hover:bg-forest-light/10"
                      >
                        Marcar lida
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(n.id)}
                      className="rounded-lg border border-energy/30 px-3 py-1.5 text-xs font-bold text-energy hover:bg-energy/10"
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-fg/10 p-4">
            <span className="text-xs text-fg/40">
              Página {page} de {totalPages} · {total} total
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border border-fg/10 px-3 py-1.5 text-xs font-bold text-fg/60 hover:bg-fg/10 disabled:opacity-30"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-fg/10 px-3 py-1.5 text-xs font-bold text-fg/60 hover:bg-fg/10 disabled:opacity-30"
              >
                Seguinte
              </button>
            </div>
          </div>
        )}
      </section>
    </section>
  );
}
